import { Link } from "react-router-dom";

import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Pill } from "../../lib/Pill";
import { useSeo } from "../../lib/useSeo";
import { STATE_RULES_MARKETING } from "./data/stateRules";

export function StatesIndexPage() {
  useSeo({
    title: "Property operations software in all 50 states + DC — NestPlot",
    description:
      "NestPlot provides a state-compliant lease the moment you create a property — across multifamily, self-storage, parking, and RV/boat. Pick your state for the late-fee cap, grace period, notice windows, and statute reference.",
    path: "/states",
  });
  return (
    <div className="mx-auto max-w-[1320px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="max-w-[760px]">
        <Pill tone="accent" size="sm">
          Live in 50 states + DC
        </Pill>
        <h1 className="t-display-xl mt-4">Property operations software, every state.</h1>
        <p className="text-lg text-sub mt-4">
          NestPlot provides a state-compliant lease the moment you create a
          property — late-fee cap, grace period, notice windows, and recovery
          timing all pulled from the statute that governs your vertical
          (landlord-tenant act for multifamily, self-storage statute for
          storage, parking lease law for lots, or marina / RV agreement for
          vehicle storage). The summary card below shows the self-storage
          statute for each state; the same per-state data shape powers every
          vertical. Pick your state for the specifics.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/signup">
            <Btn kind="primary" size="md" iconRight={<Icon name="arrow-right" size={14} />}>
              Start free · 30 days
            </Btn>
          </Link>
          <Link to="/pricing">
            <Btn kind="ghost" size="md">
              See pricing →
            </Btn>
          </Link>
        </div>
      </div>

      <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {STATE_RULES_MARKETING.map((s) => (
          <Link key={s.code} to={`/states/${s.slug}`} className="block group">
            <Card className="h-full hover:shadow-rollup transition-shadow">
              <div className="px-5 py-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display font-extrabold text-[16px] uppercase tracking-[-0.005em]">
                    {s.name}
                  </span>
                  <span className="font-mono text-[11px] text-sub">{s.code}</span>
                </div>
                <div className="text-[12.5px] text-sub mt-2 line-clamp-2">
                  {s.statute}
                </div>
                <div className="mt-3 flex gap-3 text-[11px] font-mono text-sub">
                  <span>Late fee · ${(s.lateFeeCapCents / 100).toFixed(0)}</span>
                  <span>Grace · {s.gracePeriodDays}d</span>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="mt-14 max-w-[760px]">
        <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
          The fine print
        </div>
        <h2 className="t-display-l mt-2">What "available in all 50 states" actually means.</h2>
        <p className="text-sub mt-4">
          NestPlot handles the lease, the late-fee math, the reminder cadence,
          the access flow, and the move-out — all calibrated to the statute
          that governs your vertical: landlord-tenant act for multifamily,
          self-storage statute for storage, parking lease law for lots, or
          marina / RV agreement for vehicle storage. UETA / E-SIGN signing
          covers the e-signature side in every state; New York properties
          default to a stronger identity-binding step (one-time code) out of
          the box.
        </p>
        <p className="text-sub mt-3">
          A few compliance items live with you, not us, regardless of vertical:
          (a) any insurance-licensed add-on you offer alongside the lease —
          tenant-protection plans in self-storage (NY / CA / FL treat them as
          insurance), renter's insurance referrals in multifamily, or hull
          insurance verification in marinas — NestPlot captures the customer's
          election but does not underwrite; (b) sales tax, parking surcharges,
          and other transaction taxes — we collect and itemize on the P&amp;L,
          you remit; and (c) the final step of any recovery action — court
          eviction in multifamily, lien-sale auction in self-storage (currently
          routed through third-party platforms), tow / impound in parking,
          vessel detention in marinas — runs through you and your service
          providers. We drive the notice windows and produce the required
          documents up to that step.
        </p>
      </div>
    </div>
  );
}
